import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, X, Trash2, GripVertical, DollarSign, CheckCircle2, RotateCcw, ChevronDown, ChevronRight, Camera, History } from "lucide-react";
import { useAuth } from "../lib/auth";

const ACTIVE_STAGES = [
  { key: "awaiting_payment",   label: "Awaiting Payment",    accent: "#7C3AED" },
  { key: "verbal_commitment",  label: "Verbal Commitments",  accent: "#F59E0B" },
  { key: "in_progress",        label: "Project In Progress", accent: "#0066ff" },
  { key: "sales_pipeline",     label: "Sales Pipeline",      accent: "#71717A" },
];
const PAID_STAGE = { key: "paid", label: "Paid", accent: "#10B981" };
const ALL_STAGES = [...ACTIVE_STAGES, PAID_STAGE];

const STAGE_DUE_LABEL = {
  sales_pipeline: "Due to Begin",
  verbal_commitment: "Due to Begin",
  in_progress: "Due upon Completion",
  awaiting_payment: "Awaiting Payment",
  paid: "Paid Amount",
};

const expectedRev = (l) => (typeof l.total_revenue === "number" && typeof l.confidence === "number") ? (l.total_revenue * l.confidence) / 10 : null;
const money = (v) => typeof v !== "number" ? "—" : `$${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export default function LeadPipeline() {
  const { user } = useAuth();
  const isViewer = user?.role === "viewer";
  const [leads, setLeads] = useState([]);
  const [tab, setTab] = useState("active"); // "active" | "paid"
  const [createIn, setCreateIn] = useState(null);
  const [editing, setEditing] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [selectedSnapshotId, setSelectedSnapshotId] = useState(""); // "" = live
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const viewingSnapshot = !!selectedSnapshotId;
  const readOnly = isViewer || viewingSnapshot;
  // Default every stage to collapsed so the page opens as a tidy summary.
  const [collapsed, setCollapsed] = useState(() => {
    const init = {};
    ALL_STAGES.forEach((s) => { init[s.key] = true; });
    return init;
  });
  const toggleCollapsed = (key) => setCollapsed((c) => ({ ...c, [key]: !c[key] }));

  const load = async () => {
    const { data } = await api.get("/leads");
    setLeads(data);
  };
  const loadSnapshots = async () => {
    try {
      const { data } = await api.get("/lead-snapshots");
      setSnapshots(data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadSnapshots(); }, []);

  // When a snapshot is selected, fetch its leads payload and use it instead of live data.
  useEffect(() => {
    if (!selectedSnapshotId) { load(); return; }
    let cancel = false;
    setSnapshotLoading(true);
    api.get(`/lead-snapshots/${selectedSnapshotId}`)
      .then(({ data }) => {
        if (cancel) return;
        setLeads(data.leads || []);
      })
      .catch(() => { if (!cancel) toast.error("Failed to load snapshot"); })
      .finally(() => { if (!cancel) setSnapshotLoading(false); });
    return () => { cancel = true; };
  }, [selectedSnapshotId]);

  const saveSnapshot = async () => {
    const label = window.prompt("Name this snapshot (optional)", `Manual ${new Date().toLocaleDateString()}`);
    if (label === null) return;
    try {
      const { data } = await api.post("/lead-snapshots", { label: label?.trim() || null });
      toast.success("Snapshot saved");
      await loadSnapshots();
      if (data?.id) setSelectedSnapshotId(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save snapshot");
    }
  };

  const deleteSnapshot = async () => {
    if (!selectedSnapshotId) return;
    const snap = snapshots.find((s) => s.id === selectedSnapshotId);
    if (!window.confirm(`Delete snapshot "${snap?.label || ""}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/lead-snapshots/${selectedSnapshotId}`);
      toast.success("Snapshot deleted");
      setSelectedSnapshotId("");
      await loadSnapshots();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const byStage = useMemo(() => {
    const m = {};
    ALL_STAGES.forEach((s) => { m[s.key] = []; });
    [...leads].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).forEach((l) => {
      if (m[l.stage]) m[l.stage].push(l);
    });
    return m;
  }, [leads]);

  const totals = useMemo(() => {
    const out = {
      byStage: {},
      active: { rev: 0, expRev: 0 },
      paid: { rev: 0, expRev: 0 },
    };
    ALL_STAGES.forEach((s) => {
      let rev = 0, expRev = 0;
      byStage[s.key].forEach((l) => {
        const r = expectedRev(l);
        if (typeof l.total_revenue === "number") rev += l.total_revenue;
        if (typeof r === "number") expRev += r;
      });
      out.byStage[s.key] = { rev, expRev };
      const bucket = s.key === "paid" ? out.paid : out.active;
      bucket.rev += rev;
      bucket.expRev += expRev;
    });
    return out;
  }, [byStage]);

  const moveStage = async (leadId, stage) => {
    try {
      await api.put(`/leads/${leadId}`, { stage });
      load();
    } catch {
      toast.error("Failed to move");
    }
  };

  const onDrop = async (e, stage) => {
    e.preventDefault();
    setDragOverStage(null);
    if (readOnly) return;
    const leadId = e.dataTransfer.getData("text/leadId");
    if (!leadId) return;
    const lead = leads.find((l) => l.id === leadId);
    if (!lead || lead.stage === stage) return;
    await moveStage(leadId, stage);
  };

  const activeCount = leads.filter((l) => l.stage !== "paid").length;
  const paidCount = byStage.paid.length;
  const tabTotals = tab === "active" ? totals.active : totals.paid;
  const tabLabel = tab === "active" ? "Active Pipeline" : "Paid";

  return (
    <div data-testid="lead-pipeline-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Pipeline</div>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900">Lead Pipeline</h1>
          <p className="mt-2 text-sm text-zinc-600">
            {viewingSnapshot
              ? "Viewing a historical snapshot — drag and edits are disabled."
              : isViewer
                ? "View-only access. Drag and edits are disabled."
                : "Drag leads through your sales stages. Expected values are weighted by confidence."}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 rounded-lg border border-zinc-200 bg-white px-3 py-2">
            <History size={14} className={viewingSnapshot ? "text-amber-600" : "text-zinc-400"} />
            <select
              data-testid="snapshot-select"
              value={selectedSnapshotId}
              onChange={(e) => setSelectedSnapshotId(e.target.value)}
              className="bg-transparent text-xs font-medium text-zinc-700 focus:outline-none"
            >
              <option value="">Live pipeline</option>
              {snapshots.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.kind === "auto" ? "[Auto] " : ""}{s.label || s.period || s.id.slice(0, 6)}
                </option>
              ))}
            </select>
            {viewingSnapshot && !isViewer && (
              <button
                data-testid="snapshot-delete"
                onClick={deleteSnapshot}
                title="Delete this snapshot"
                className="rounded p-1 text-zinc-400 hover:bg-red-50 hover:text-red-600"
              >
                <Trash2 size={13} />
              </button>
            )}
          </div>
          {!isViewer && (
            <button
              data-testid="snapshot-save"
              onClick={saveSnapshot}
              disabled={viewingSnapshot}
              className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 py-2 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
            >
              <Camera size={13} /> Save Snapshot
            </button>
          )}
        </div>
      </div>

      {viewingSnapshot && (
        <div data-testid="snapshot-banner" className="mt-4 flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50/70 px-4 py-3 text-xs text-amber-900">
          <History size={14} className="text-amber-700" />
          <div className="flex-1">
            <strong>Historical snapshot.</strong>{" "}
            {(() => {
              const s = snapshots.find((x) => x.id === selectedSnapshotId);
              if (!s) return "Read-only view.";
              const dt = s.created_at ? new Date(s.created_at).toLocaleString() : "";
              return `Captured ${dt}${s.kind === "auto" ? " · auto end-of-month" : " · manual"}. Read-only view.`;
            })()}
          </div>
          <button
            data-testid="snapshot-return-live"
            onClick={() => setSelectedSnapshotId("")}
            className="rounded-md bg-white px-3 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100"
          >
            Return to live
          </button>
        </div>
      )}

      {snapshotLoading && (
        <div className="mt-3 text-xs text-zinc-500">Loading snapshot…</div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-2">
        <SummaryCard
          testId="lead-pipeline-total-rev"
          icon={DollarSign}
          accent="text-emerald-700"
          label={`${tabLabel} Revenue`}
          value={money(tabTotals.rev)}
        />
        <SummaryCard
          testId="lead-pipeline-expected-rev"
          icon={DollarSign}
          accent="text-[#0066ff]"
          label="Expected Revenue"
          value={money(tabTotals.expRev)}
          note="= revenue × confidence ÷ 10"
        />
      </div>

      <div className="mt-6 flex items-center gap-1 rounded-lg border border-zinc-200 bg-white p-1 w-fit">
        {[
          { id: "active", label: "Active", count: activeCount },
          { id: "paid", label: "Paid", count: paidCount },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`lead-pipeline-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              tab === t.id ? "bg-[#E5F0FF] text-[#0066ff]" : "text-zinc-600 hover:bg-zinc-50"
            }`}
          >
            {t.label}
            <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${tab === t.id ? "bg-white text-[#0066ff]" : "bg-zinc-100 text-zinc-500"}`}>
              {t.count}
            </span>
          </button>
        ))}
      </div>

      {tab === "active" ? (
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
          {ACTIVE_STAGES.map((s) => (
            <StageColumn
              key={s.key}
              stage={s}
              leads={byStage[s.key]}
              totals={totals.byStage[s.key]}
              dragOver={dragOverStage === s.key}
              collapsed={!!collapsed[s.key]}
              onToggleCollapse={() => toggleCollapsed(s.key)}
              onDragOver={(e) => { e.preventDefault(); if (!readOnly) setDragOverStage(s.key); }}
              onDragLeave={() => setDragOverStage((c) => c === s.key ? null : c)}
              onDrop={(e) => onDrop(e, s.key)}
              onAdd={readOnly ? null : () => setCreateIn(s.key)}
              onOpen={(l) => setEditing(l)}
              onMarkPaid={readOnly ? null : (l) => moveStage(l.id, "paid")}
              readOnly={readOnly}
            />
          ))}
        </div>
      ) : (
        <div className="mt-6">
          <StageColumn
            stage={PAID_STAGE}
            leads={byStage.paid}
            totals={totals.byStage.paid}
            dragOver={false}
            collapsed={!!collapsed.paid}
            onToggleCollapse={() => toggleCollapsed("paid")}
            onDragOver={(e) => e.preventDefault()}
            onDragLeave={() => {}}
            onDrop={() => {}}
            onAdd={null}
            onOpen={(l) => setEditing(l)}
            onUnpay={readOnly ? null : (l) => moveStage(l.id, "awaiting_payment")}
            readOnly={readOnly}
            fullWidth
          />
        </div>
      )}

      {createIn && (
        <LeadModal mode="create" defaultStage={createIn} onClose={() => setCreateIn(null)} onSaved={() => { setCreateIn(null); load(); }} />
      )}
      {editing && (
        <LeadModal
          mode="edit"
          initial={editing}
          readOnly={readOnly}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          onDeleted={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function SummaryCard({ testId, icon: Icon, accent, label, value, note }) {
  return (
    <div data-testid={testId} className="rounded-lg border border-zinc-200 bg-white p-4">
      <div className="flex items-start gap-2">
        <Icon size={14} className={accent} />
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">{label}</div>
          <div className={`mt-0.5 text-xl font-semibold ${accent}`}>{value}</div>
          {note && <div className="mt-1 text-[10px] italic text-zinc-400">{note}</div>}
        </div>
      </div>
    </div>
  );
}

function StageColumn({ stage, leads, totals, dragOver, collapsed, onToggleCollapse, onDragOver, onDragLeave, onDrop, onAdd, onOpen, onMarkPaid, onUnpay, readOnly, fullWidth }) {
  return (
    <div
      data-testid={`lead-column-${stage.key}`}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`flex flex-col rounded-xl border bg-white ${dragOver ? "border-[#0066ff] bg-[#E5F0FF]/30 ring-2 ring-[#0066ff]/20" : "border-zinc-200"} ${collapsed ? "min-h-0" : "min-h-[300px]"}`}
    >
      <div className="flex items-center justify-between gap-3 border-b border-zinc-100 px-4 py-3" style={{ borderTopColor: stage.accent, borderTopWidth: 3, borderTopStyle: "solid" }}>
        <button
          type="button"
          data-testid={`lead-column-${stage.key}-toggle`}
          onClick={onToggleCollapse}
          aria-expanded={!collapsed}
          aria-label={collapsed ? `Expand ${stage.label}` : `Collapse ${stage.label}`}
          className="group flex flex-1 items-center gap-3 rounded-md py-1 text-left transition-colors hover:bg-zinc-50"
        >
          <span
            className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-white shadow-sm transition-transform group-hover:scale-110"
            style={{ backgroundColor: stage.accent }}
          >
            {collapsed ? <ChevronRight size={16} strokeWidth={2.5} /> : <ChevronDown size={16} strokeWidth={2.5} />}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2">
              <span className="text-sm font-semibold tracking-tight" style={{ color: stage.accent }}>{stage.label}</span>
              <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-semibold text-zinc-600">{leads.length}</span>
              {collapsed && leads.length > 0 && (
                <span className="text-[10px] font-medium uppercase tracking-widest text-[#0066ff] underline decoration-dotted underline-offset-4 group-hover:text-[#0052cc]">
                  Click to expand
                </span>
              )}
            </div>
            <div className="mt-0.5 text-[10px] text-zinc-500">
              Total Rev {money(totals.rev)} · Exp. {money(totals.expRev)}
            </div>
          </div>
        </button>
        {onAdd && !collapsed && (
          <button
            data-testid={`lead-add-${stage.key}`}
            onClick={onAdd}
            className="rounded-md p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-[#0066ff]"
            title="Add lead"
          >
            <Plus size={14} />
          </button>
        )}
      </div>

      {collapsed ? (
        <div data-testid={`lead-column-${stage.key}-collapsed`} className="flex flex-col gap-1 p-3">
          {leads.length === 0 && (
            <div className="rounded-md border border-dashed border-zinc-200 px-3 py-2 text-center text-[11px] text-zinc-400">No leads</div>
          )}
          {leads.map((l) => (
            <button
              key={l.id}
              data-testid={`lead-row-${l.id}`}
              onClick={() => onOpen(l)}
              className="flex items-center justify-between gap-3 rounded-md border border-zinc-100 bg-white px-3 py-1.5 text-left text-xs transition-colors hover:bg-zinc-50"
            >
              <span className="min-w-0 flex-1 truncate font-medium text-zinc-800">{l.client}</span>
              <span className="text-[10px] text-zinc-400">Conf {l.confidence}/10</span>
              <span className="font-mono text-[11px] text-zinc-700">{typeof l.total_revenue === "number" ? money(l.total_revenue) : "—"}</span>
            </button>
          ))}
        </div>
      ) : (
        <div className={`grid gap-2 p-3 ${fullWidth ? "grid-cols-1 md:grid-cols-2 xl:grid-cols-3" : "grid-cols-1"}`}>
          {leads.map((l) => (
            <LeadCard
              key={l.id}
              lead={l}
              accent={stage.accent}
              onOpen={() => onOpen(l)}
              onMarkPaid={onMarkPaid ? () => onMarkPaid(l) : null}
              onUnpay={onUnpay ? () => onUnpay(l) : null}
              readOnly={readOnly}
            />
          ))}
          {leads.length === 0 && (
            <div className={`rounded-md border border-dashed border-zinc-200 px-3 py-6 text-center text-xs text-zinc-400 ${fullWidth ? "col-span-full" : ""}`}>
              {readOnly ? "No leads" : (stage.key === "paid" ? "No paid leads yet" : "Drop or add a lead")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LeadCard({ lead, accent, onOpen, onMarkPaid, onUnpay, readOnly }) {
  const expRev = expectedRev(lead);
  const isPaid = lead.stage === "paid";
  return (
    <div
      draggable={!readOnly && !isPaid}
      onDragStart={(e) => e.dataTransfer.setData("text/leadId", lead.id)}
      onClick={onOpen}
      data-testid={`lead-card-${lead.id}`}
      className={`group relative cursor-pointer rounded-lg border border-zinc-200 bg-white p-3 shadow-sm transition-all hover:-translate-y-[1px] hover:shadow-md ${isPaid ? "opacity-80" : ""}`}
      style={{ borderLeft: `3px solid ${accent}` }}
    >
      {isPaid && (
        <div className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-md bg-emerald-500 shadow-sm">
          <CheckCircle2 size={12} className="text-white" strokeWidth={2.5} />
        </div>
      )}
      <div className="flex items-start justify-between gap-2 pr-6">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-zinc-900">{lead.client}</div>
          {lead.project_type && <div className="mt-0.5 truncate text-[11px] text-zinc-500">{lead.project_type}</div>}
        </div>
        {!readOnly && !isPaid && <GripVertical size={12} className="mt-1 text-zinc-300 opacity-0 transition-opacity group-hover:opacity-100" />}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-1 text-[10px]">
        <span className="inline-flex items-center rounded-full bg-zinc-100 px-2 py-0.5 font-medium text-zinc-700">
          Confidence {lead.confidence}/10
        </span>
        {lead.source && (
          <span className="inline-flex max-w-[160px] items-center truncate rounded-full bg-zinc-50 px-2 py-0.5 text-zinc-500">{lead.source}</span>
        )}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-[10px]">
        <Metric label="Total Rev" value={typeof lead.total_revenue === "number" ? money(lead.total_revenue) : "—"} />
        <Metric label="Exp. Rev" value={typeof expRev === "number" ? money(expRev) : "—"} accent="text-[#0066ff]" />
      </div>
      {lead.notes && <div className="mt-2 line-clamp-2 text-[11px] text-zinc-500">{lead.notes}</div>}
      {!readOnly && (onMarkPaid || onUnpay) && (
        <div className="mt-3 flex items-center justify-end border-t border-zinc-100 pt-2">
          {onMarkPaid && (
            <button
              data-testid={`lead-card-${lead.id}-mark-paid`}
              onClick={(e) => { e.stopPropagation(); onMarkPaid(); }}
              className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-1 text-[10px] font-semibold text-emerald-700 hover:bg-emerald-100"
            >
              <CheckCircle2 size={11} /> Mark Paid
            </button>
          )}
          {onUnpay && (
            <button
              data-testid={`lead-card-${lead.id}-unpay`}
              onClick={(e) => { e.stopPropagation(); onUnpay(); }}
              className="inline-flex items-center gap-1 rounded-md bg-zinc-100 px-2 py-1 text-[10px] font-semibold text-zinc-700 hover:bg-zinc-200"
            >
              <RotateCcw size={11} /> Move to Awaiting Payment
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, accent = "text-zinc-900" }) {
  return (
    <div className="rounded border border-zinc-100 bg-zinc-50/50 px-2 py-1">
      <div className="text-[9px] uppercase tracking-wider text-zinc-400">{label}</div>
      <div className={`font-semibold ${accent}`}>{value}</div>
    </div>
  );
}

function LeadModal({ mode = "create", initial = null, defaultStage = "sales_pipeline", readOnly = false, onClose, onSaved, onDeleted }) {
  const [form, setForm] = useState(
    initial || {
      client: "",
      project_type: "",
      source: "",
      stage: defaultStage,
      total_revenue: "",
      due_amount: "",
      confidence: 5,
      notes: "",
    }
  );
  const [saving, setSaving] = useState(false);
  const set = (k, v) => { if (!readOnly) setForm((f) => ({ ...f, [k]: v })); };

  const numOrNull = (v) => {
    if (v === "" || v === null || v === undefined) return null;
    const n = parseFloat(v);
    return isNaN(n) ? null : n;
  };

  const save = async () => {
    if (readOnly) return;
    if (!form.client.trim()) { toast.error("Client name required"); return; }
    const payload = {
      ...form,
      total_revenue: numOrNull(form.total_revenue),
      due_amount: numOrNull(form.due_amount),
      total_profit: null,
      confidence: parseInt(form.confidence),
    };
    setSaving(true);
    try {
      if (mode === "create") await api.post("/leads", payload);
      else await api.put(`/leads/${initial.id}`, payload);
      toast.success(mode === "create" ? "Lead added" : "Saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (readOnly || !initial || !window.confirm(`Delete "${initial.client}"?`)) return;
    try {
      await api.delete(`/leads/${initial.id}`);
      toast.success("Deleted");
      onDeleted();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const previewRev = numOrNull(form.total_revenue) !== null ? (numOrNull(form.total_revenue) * parseInt(form.confidence)) / 10 : null;
  const fieldDisabled = readOnly;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div data-testid="lead-modal" className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white p-8 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">{readOnly ? "View" : (mode === "create" ? "New" : "Edit")}</div>
            <h2 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-900">Lead</h2>
          </div>
          <button onClick={onClose} className="rounded p-1 text-zinc-400 hover:bg-zinc-100"><X size={18} /></button>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4">
          <Field label="Client" required>
            <input data-testid="lead-form-client" disabled={fieldDisabled} autoFocus value={form.client} onChange={(e) => set("client", e.target.value)} className={inputCls} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Project Type">
              <input data-testid="lead-form-project-type" disabled={fieldDisabled} value={form.project_type} onChange={(e) => set("project_type", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Source">
              <input data-testid="lead-form-source" disabled={fieldDisabled} value={form.source} onChange={(e) => set("source", e.target.value)} className={inputCls} />
            </Field>
          </div>
          <Field label="Stage">
            <select data-testid="lead-form-stage" disabled={fieldDisabled} value={form.stage} onChange={(e) => set("stage", e.target.value)} className={inputCls}>
              {ALL_STAGES.map((s) => (<option key={s.key} value={s.key}>{s.label}</option>))}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Total Revenue ($)">
              <input data-testid="lead-form-total-revenue" disabled={fieldDisabled} type="number" step="0.01" value={form.total_revenue ?? ""} onChange={(e) => set("total_revenue", e.target.value)} className={inputCls} placeholder="—" />
            </Field>
            <Field label={STAGE_DUE_LABEL[form.stage] + " ($)"}>
              <input data-testid="lead-form-due" disabled={fieldDisabled} type="number" step="0.01" value={form.due_amount ?? ""} onChange={(e) => set("due_amount", e.target.value)} className={inputCls} placeholder="—" />
            </Field>
          </div>
          <Field label={`Confidence (${form.confidence}/10)`}>
            <input data-testid="lead-form-confidence" disabled={fieldDisabled} type="range" min="0" max="10" step="1" value={form.confidence} onChange={(e) => set("confidence", e.target.value)} className="w-full accent-[#0066ff]" />
          </Field>

          {previewRev !== null && (
            <div className="rounded-lg bg-[#E5F0FF]/40 p-3 text-xs">
              <div className="font-semibold text-zinc-600">Expected Revenue</div>
              <div className="mt-0.5 text-base font-semibold text-[#0066ff]">{money(previewRev)}</div>
              <div className="mt-1 text-[10px] text-zinc-500">= total revenue × confidence ÷ 10</div>
            </div>
          )}

          <Field label="Notes">
            <textarea data-testid="lead-form-notes" disabled={fieldDisabled} rows={4} value={form.notes ?? ""} onChange={(e) => set("notes", e.target.value)} className={inputCls} />
          </Field>
        </div>

        <div className="mt-8 flex items-center justify-between gap-3">
          <div>
            {!readOnly && mode === "edit" && (
              <button data-testid="lead-delete" onClick={remove} className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline"><Trash2 size={12} /> Delete</button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="rounded-md px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-100">{readOnly ? "Close" : "Cancel"}</button>
            {!readOnly && (
              <button data-testid="lead-modal-save" onClick={save} disabled={saving} className="rounded-md bg-[#0066ff] px-4 py-2 text-sm font-medium text-white hover:bg-[#0052cc] disabled:opacity-50">
                {saving ? "Saving…" : (mode === "create" ? "Add Lead" : "Save")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm placeholder:text-zinc-400 focus:border-[#0066ff] focus:outline-none focus:ring-2 focus:ring-[#0066ff]/20 disabled:cursor-not-allowed disabled:bg-zinc-50 disabled:text-zinc-500";

function Field({ label, required, children }) {
  return (
    <label className="flex flex-col gap-1.5 text-sm">
      <span className="text-xs font-medium text-zinc-600">{label}{required && <span className="text-red-500"> *</span>}</span>
      {children}
    </label>
  );
}
